import { NavLink, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import {
  LayoutDashboard, ListTodo, MessageSquare, MessageCircle, Sparkles, Phone, Smile, ClipboardCheck,
  Mail, Users, CheckSquare, Ticket, UserCog, Building2,
  Calendar, LogOut, ChevronLeft, ChevronRight, BrainCircuit,
  History, Shield, Zap, Bell, BellOff, Wallet, LineChart, Search, ListChecks, Menu, X,
  NotebookPen, KeyRound, Users2, Lightbulb, CalendarClock, ChevronDown, BookOpen, FileText, Handshake,
} from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { tenantFarbe } from "@/lib/tenantFarbe"
import { toast } from "@/components/portal/Toast"

/**
 * Portal-Sidebar — 1:1-Port von admin/templates.py:ADMIN_NAV.
 *
 * 15 Nav-Items (Dashboard, Assistent, Smart Inbox, Anrufe, KI Telefon,
 * E-Mails, Kontakte, Aufgaben, Tickets, System-Status, Kunden, Tenants,
 * Kalender, Projekte) + Logout-Footer.
 *
 * Farben:
 *   Background #0f172a (sidebar-bg)
 *   Text #94a3b8 (sidebar-text)
 *   Active-Text #a5b4fc mit indigo-Tint-BG
 */

type NavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  end?: boolean
  /** Schluessel fuer das Ungelesen-Badge; ohne = kein Badge. */
  bereich?: string
  /**
   * Nur sichtbar, wer dieses Recht hat (#27, ab 25.08. statt `nurAdmin`).
   *
   * 🔴 Das ist Bequemlichkeit, KEINE Sperre. Der Riegel sitzt im Backend
   * (`require_recht` in core/deps.py) — ein ausgeblendeter Menuepunkt haelt
   * niemanden auf, der die URL kennt.
   *
   * 🔑 Der Schluessel muss einer aus `core/rechte.py` sein. Ein Tippfehler
   * blendet den Punkt fuer alle aus, ohne dass irgendwo etwas rot wird.
   */
  recht?: string
}

/**
 * Gruppen statt einer flachen Liste (#119, Paul 24.09.2026: „Die Navigation
 * links wird langsam bisschen groß"). 24 Punkte untereinander liest niemand
 * mehr — drei Gruppen, jede auf- und zuklappbar, der Zustand bleibt im Browser.
 *
 *   Arbeit      das Tagesgeschaeft mit den Kunden
 *   Intern      werkXpartner selbst: Dienstplan, interne Aufgaben, Backlog,
 *               Verbessern, Kosten
 *   Verwaltung  Einstellungen, Zugaenge, Sicherheit
 *
 * Die Gruppe des aktiven Punkts ist immer offen — sonst stuende man auf einer
 * Seite, die in der Leiste nicht zu sehen ist.
 */
export type NavGruppe = { schluessel: string; label: string; items: NavItem[] }

export const NAV_GRUPPEN: NavGruppe[] = [
  {
    schluessel: "arbeit", label: "Arbeit", items: [
      { to: "/todo", label: "To-Do", icon: ListTodo },
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/chat", label: "Assistent", icon: MessageSquare },
      { to: "/review", label: "Review", icon: Sparkles },
      { to: "/emails", label: "E-Mails", icon: Mail, bereich: "emails" },
      { to: "/sipgate", label: "Anrufe", icon: Phone, bereich: "sipgate" },
      { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, bereich: "whatsapp" },
      // KI-Angebote (08.10.2026): Entwurf aus frueheren Angeboten, dann nach HERO.
      { to: "/angebote", label: "KI-Angebote", icon: FileText, recht: "angebote" },
      // „Meiti Anrufe" (/calls) raus (#226, Marlene 02.11.2026): Sanitec war der
      // einzige Betrieb mit Meiti und braucht die Rubrik nicht mehr. Die Seite
      // bleibt ueber die Adresse erreichbar.
      { to: "/ki-telefon", label: "KI Telefon", icon: Smile, bereich: "ki-telefon" },
      { to: "/rueckmeldungen", label: "Rueckmeldungen", icon: ClipboardCheck, bereich: "rueckmeldungen" },
      { to: "/tasks", label: "Aufgaben", icon: CheckSquare, bereich: "aufgaben" },
      { to: "/calendar", label: "Kalender", icon: Calendar },
      { to: "/contacts", label: "Kontakte", icon: Users },
      { to: "/projekte", label: "Suche", icon: Search },
      { to: "/tickets", label: "Tickets", icon: Ticket },
    ],
  },
  {
    schluessel: "intern", label: "Intern", items: [
      // Dienstplan (#119): wer wann Dienst hat, wer im Urlaub ist.
      { to: "/dienstplan", label: "Dienstplan", icon: CalendarClock },
      // Interne Aufgaben (#33): gehen nicht nach HERO — das Buero fuer sich.
      { to: "/interne-aufgaben", label: "Interne Aufgaben", icon: NotebookPen },
      // Vertrieb (Paul 26.10.2026): eigene Leads und Deals, HubSpot-Ersatz.
      // Das Badge zaehlt, was heute dran ist — nicht „ungelesen".
      { to: "/vertrieb", label: "Vertrieb", icon: Handshake, recht: "vertrieb", bereich: "vertrieb" },
      // Wissensdatenbank (Paul 01.10.2026): was der Betrieb aus seinen Mails
      // gelernt hat — geprüft; der Planer nutzt nur, was „gilt".
      { to: "/wissensdatenbank", label: "Wissensdatenbank", icon: BookOpen },
      // Backlog nur fuer Administratoren (Paul 26.10.2026).
      { to: "/backlog", label: "Backlog", icon: ListChecks, recht: "backlog" },
      // „Verbessern" (Paul 11.09.2026): Wege (#44), Vorschlagsqualitaet und
      // Rueckkopplung (#64) unter EINEM Punkt. Kein Badge: hier wartet nichts.
      { to: "/verbessern", label: "Verbessern", icon: Lightbulb },
      { to: "/kosten", label: "Kosten", icon: Wallet, recht: "kosten" },
      // Controlling (Paul 25.10.2026): echte Zahlen aus BuchhaltungsButler.
      // Nur namentlich freigegebene Zugaenge, siehe core/rechte.py.
      { to: "/controlling", label: "Controlling", icon: LineChart, recht: "controlling" },
    ],
  },
  {
    schluessel: "verwaltung", label: "Verwaltung", items: [
      { to: "/context", label: "Kontext-Center", icon: BrainCircuit },
      { to: "/kunden", label: "Kunden", icon: UserCog },
      { to: "/tenants", label: "Tenants", icon: Building2, recht: "mandanten" },
      { to: "/auto-switches", label: "Auto-Switches", icon: Zap },
      // Zugaenge direkt bei Sicherheit — derselbe Themenkreis (#27).
      { to: "/zugaenge", label: "Zugänge", icon: Users2, recht: "zugaenge" },
      { to: "/security", label: "Sicherheit", icon: Shield, recht: "sicherheit" },
      { to: "/aktivitaet", label: "Aktivitaet", icon: History, bereich: "aktivitaet" },
    ],
  },
]

/** Flache Sicht fuer alles, was nur die Punkte braucht (Badges, Tests). */
const NAV: NavItem[] = NAV_GRUPPEN.flatMap((g) => g.items)

const GRUPPEN_SPEICHER = "nav_gruppen_zu"

/**
 * localStorage kann werfen (privates Fenster, gesperrte Website-Daten,
 * Speicher voll). Die Leiste darf daran nie scheitern — sonst ist das ganze
 * Portal weg, nicht nur die gemerkte Einklapp-Wahl (#96).
 */
function speicherLesen(schluessel: string): string | null {
  try { return localStorage.getItem(schluessel) } catch { return null }
}
function speicherSchreiben(schluessel: string, wert: string | null): void {
  try {
    if (wert === null) localStorage.removeItem(schluessel)
    else localStorage.setItem(schluessel, wert)
  } catch { /* dann eben nicht gemerkt */ }
}

function geschlosseneGruppen(): Set<string> {
  try {
    const roh = localStorage.getItem(GRUPPEN_SPEICHER)
    return new Set(roh ? (JSON.parse(roh) as string[]) : [])
  } catch {
    return new Set()
  }
}

/** Zu welcher Gruppe ein Pfad gehoert — laengster passender Punkt gewinnt
 *  („/sipgate/12" liegt bei Anrufe, nicht beim Dashboard). */
export function gruppeFuerPfad(pfad: string): string | null {
  let beste: { laenge: number; gruppe: string } | null = null
  for (const g of NAV_GRUPPEN) {
    for (const it of g.items) {
      const passt = it.end ? pfad === it.to : (pfad === it.to || pfad.startsWith(it.to + "/"))
      if (passt && (!beste || it.to.length > beste.laenge)) beste = { laenge: it.to.length, gruppe: g.schluessel }
    }
  }
  return beste?.gruppe ?? null
}

/**
 * Ab wann die Leiste Platz abgibt (23.08.2026).
 *
 * Gemessen auf dev: Bei 1280 px verliert die Mailliste 40 % ihrer Breite,
 * waehrend Navigation und Ordnerspalte ihre vollen 520 px behalten — obwohl
 * darin nur kurze Woerter stehen. Die Navigation ist deshalb der erste
 * Bereich, der nachgibt.
 *
 *   >= 1400   volle Breite (240 px)
 *   900–1399  nur Symbole (64 px)  → gewinnt 176 px fuer den Inhalt
 *   < 900     ganz weg, per Knopf als Overlay
 */
const NUR_SYMBOLE_AB = 1400
const GANZ_WEG_AB = 900

function fensterbreite(): number {
  return typeof window === "undefined" ? 1440 : window.innerWidth
}

export function PortalSidebar() {
  const { logout } = useAuth()
  const [breite, setBreite] = useState(fensterbreite)
  // Eine bewusste Entscheidung des Nutzers schlaegt die Automatik — aber nur
  // solange er in derselben Groessenordnung bleibt. Zieht er das Fenster ueber
  // eine Schwelle, gilt wieder die Automatik. Sonst muesste man nach jedem
  // Monitorwechsel von Hand nachhelfen.
  // Bewusst ein NEUER Schluessel: Der alte `sidebar_collapsed` hatte eine
  // andere Bedeutung — er war die einzige Quelle, es gab keine Automatik.
  // Wuerde man ihn weiterlesen, bekaeme jeder, der die Leiste jemals von Hand
  // ausgeklappt hat, die Automatik nie zu sehen.
  const [wahl, setWahl] = useState<boolean | null>(() => {
    const g = speicherLesen("nav_wahl")
    return g === "ein" ? true : g === "aus" ? false : null
  })
  const [overlayOffen, setOverlayOffen] = useState(false)
  const [notify, setNotify] = useState(() => speicherLesen("notifyAlerts") !== "off")

  const overlayModus = breite < GANZ_WEG_AB
  const collapsed = overlayModus ? false : (wahl ?? breite < NUR_SYMBOLE_AB)

  // Die ganze Leiste traegt den Ton des Mandanten (#43 Teil 2). Ohne Auswahl
  // bleibt sie neutral — dann gibt es nichts zu verwechseln.
  const [tonSlug] = useTenantFilter()
  const tonFarbe = tenantFarbe(tonSlug)
  const sidebarTon = tonFarbe
    ? {
        // #60 (Paul, 27.08.): der Schimmer aus #43 war zu dezent — „Das ganze
        // Menü sollte die Farbe ändern, je nach tenant". Jetzt Vollflaeche im
        // dunklen Mandanten-Ton: helle Schrift bleibt lesbar, aber die Leiste
        // ist unuebersehbar die des gewaehlten Mandanten.
        backgroundImage:
          `linear-gradient(180deg, ${tonFarbe.flaecheHell} 0%, ${tonFarbe.flaeche} 100%)`,
        borderRight: `3px solid ${tonFarbe.rand}`,
      }
    : undefined

  useEffect(() => {
    let letzteStufe = stufe(fensterbreite())
    function stufe(b: number) {
      return b < GANZ_WEG_AB ? 0 : b < NUR_SYMBOLE_AB ? 1 : 2
    }
    const onResize = () => {
      const b = fensterbreite()
      setBreite(b)
      // Schwelle ueberschritten: Automatik uebernimmt wieder.
      const s = stufe(b)
      if (s !== letzteStufe) {
        letzteStufe = s
        setWahl(null)
        speicherSchreiben("nav_wahl", null)
      }
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const toggleNotify = () => {
    setNotify((on) => {
      const next = !on
      speicherSchreiben("notifyAlerts", next ? "on" : "off")
      toast(next ? "Benachrichtigungen an (Ton + Badge)" : "Benachrichtigungen aus", next ? "success" : "warn")
      return next
    })
  }

  useEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", collapsed && !overlayModus)
    document.body.classList.toggle("sidebar-aus", overlayModus)
  }, [collapsed, overlayModus])

  // ── Ungelesen-Badges ──────────────────────────────────────────────────────
  // Semantik: "neu seit letztem Blick". Beim Aufruf eines Bereichs wird er als
  // gelesen markiert und das Badge verschwindet — auch wenn darunter noch
  // unerledigte Vorgaenge liegen. Die stehen weiterhin in der jeweiligen Liste.
  // Rechte des angemeldeten Nutzers (#27). Steuern nur, was in der Leiste
  // steht — die Sperre selbst sitzt im Backend (`require_recht`).
  //
  // 🔑 Solange die Rechte unbekannt sind, wird NICHTS ausgeblendet. Ein kurz
  // fehlender Menuepunkt sieht aus wie ein Fehler; ein kurz zu viel gezeigter
  // kostet beim Klick einen 403 und sonst nichts.
  const kontoQ = useQuery<{ benutzer: string; rolle: string; rechte?: string[] }>({
    queryKey: ["konto"],
    queryFn: async () => {
      const r = await fetch("/api/v1/konto", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })
  const rechte = kontoQ.data?.rechte

  // Vertrieb: was heute dran ist. Bleibt stehen, bis es erledigt ist — anders
  // als die Ungelesen-Badges, die beim Aufruf verschwinden.
  const crmQ = useQuery<{ anzahl: number }>({
    queryKey: ["crm-faellig"],
    queryFn: async () => {
      const r = await fetch("/api/v1/crm/faellig", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!rechte?.includes("vertrieb"),
    refetchInterval: 60_000,
  })

  // #271: neue Antworten des Inhabers — bleiben am Menüpunkt Aufgaben stehen, bis jemand „Gesehen“ klickt.
  // Fehlt die API (noch nicht ausgerollt), zählt das still als 0.
  const antwortenQ = useQuery<{ neue_antworten: number }>({
    queryKey: ["inhaber-fragen", "zaehler"],
    queryFn: async () => {
      const r = await fetch("/api/v1/inhaber-fragen/zaehler", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    retry: false,
    refetchInterval: 60_000,
  })

  const [ungeleseneBadges, setBadges] = useState<Record<string, number>>({})
  const badges: Record<string, number> = {
    ...ungeleseneBadges,
    vertrieb: crmQ.data?.anzahl || 0,
    aufgaben: (ungeleseneBadges.aufgaben || 0) + (antwortenQ.data?.neue_antworten || 0),
  }
  const location = useLocation()
  // Zugeklappte Gruppen (#119). Die Gruppe des aktiven Punkts ist immer offen.
  const [zu, setZu] = useState<Set<string>>(geschlosseneGruppen)
  const aktiveGruppe = gruppeFuerPfad(location.pathname)
  const gruppeUmschalten = (schluessel: string) => {
    setZu((alt) => {
      const neu = new Set(alt)
      if (neu.has(schluessel)) neu.delete(schluessel)
      else neu.add(schluessel)
      try { localStorage.setItem(GRUPPEN_SPEICHER, JSON.stringify(Array.from(neu))) } catch { /* egal */ }
      return neu
    })
  }

  const ladeBadges = async () => {
    try {
      const r = await fetch("/api/v1/badges", { credentials: "include" })
      if (!r.ok) return
      const d = await r.json()
      setBadges(d.counts || {})
    } catch {
      /* Sidebar darf daran nicht scheitern */
    }
  }

  useEffect(() => {
    ladeBadges()
    const t = setInterval(ladeBadges, 60_000)
    return () => clearInterval(t)
  }, [])

  // Beim Betreten eines Bereichs: als gelesen markieren und Zahl sofort
  // entfernen (optimistisch), damit es sich nicht traege anfuehlt.
  useEffect(() => {
    const item = NAV.find((n) => n.bereich && n.to === location.pathname)
    if (!item?.bereich) return
    const key = item.bereich
    setBadges((b) => (b[key] ? { ...b, [key]: 0 } : b))
    fetch(`/api/v1/badges/seen/${key}`, { method: "POST", credentials: "include" })
      .catch(() => { /* egal, naechster Poll korrigiert */ })
  }, [location.pathname])

  // Nach einem Klick auf einen Navigationspunkt soll die Leiste wieder aus dem
  // Weg sein — sonst verdeckt sie genau die Seite, die man eben geoeffnet hat.
  useEffect(() => { setOverlayOffen(false) }, [location.pathname])

  // Escape schliesst das Overlay. Erwartung bei allem, was sich ueber den
  // Inhalt legt.
  useEffect(() => {
    if (!overlayOffen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOverlayOffen(false) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [overlayOffen])

  return (
    <>
    {overlayModus && !overlayOffen && (
      <button
        type="button"
        className="pp-nav-oeffner"
        onClick={() => setOverlayOffen(true)}
        title="Navigation einblenden"
        aria-label="Navigation einblenden"
      >
        <Menu className="w-5 h-5" />
      </button>
    )}
    {overlayModus && overlayOffen && (
      <button
        type="button"
        className="pp-nav-hinterlegung"
        onClick={() => setOverlayOffen(false)}
        tabIndex={-1}
        aria-label="Navigation schliessen"
      />
    )}
    <aside
      // Menü + Betriebe (keine Endkunden) — im Bildschirmfoto lesbar
      data-frei
      className={cn(
        "pp-sidebar",
        collapsed && "collapsed",
        overlayModus && "overlay",
        overlayModus && overlayOffen && "offen",
      )}
      // #43 Teil 2 (Marlene) → #60 (Paul, 27.08.): der Ton kommt weiter aus
      // `tenantFarbe` — abgeleitet, nicht gepflegt —, aber als VOLLFLAECHE
      // statt Schimmer: der Schimmer war zu dezent, um Mandanten auf einen
      // Blick zu unterscheiden. Der Anstrich ist dunkel (L 16–24 %), damit die
      // helle Navigation lesbar bleibt.
      style={sidebarTon}
    >
      <div className="pp-sidebar-brand">
        <div className="flex items-center gap-2">
          <PortalLogo />
        </div>
        {!collapsed && <span>Admin Portal</span>}
        <button
          type="button"
          onClick={() => {
            if (overlayModus) { setOverlayOffen(false); return }
            const neu = !collapsed
            setWahl(neu)
            speicherSchreiben("nav_wahl", neu ? "ein" : "aus")
          }}
          title={overlayModus ? "Navigation schliessen" : collapsed ? "Navigation ausklappen" : "Navigation einklappen"}
          aria-label={overlayModus ? "Navigation schliessen" : collapsed ? "Navigation ausklappen" : "Navigation einklappen"}
          className="pp-sidebar-toggle"
        >
          {overlayModus
            ? <X className="w-3.5 h-3.5" />
            : collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </div>

      <MandantStreifen collapsed={collapsed} />

      <nav className="pp-sidebar-nav">
        {NAV_GRUPPEN.map((gruppe) => {
          const sichtbar = gruppe.items.filter((item) => !item.recht || !rechte || rechte.includes(item.recht))
          if (sichtbar.length === 0) return null
          const aktiv = aktiveGruppe === gruppe.schluessel
          // Eingeklappt (nur Symbole) gibt es nichts zu klappen — alle Punkte
          // stehen da, getrennt durch eine feine Linie.
          const offen = collapsed || aktiv || !zu.has(gruppe.schluessel)
          const ungelesen = sichtbar.reduce((s, it) => s + (it.bereich ? (badges[it.bereich] || 0) : 0), 0)
          return (
            <div key={gruppe.schluessel} className={cn("pp-nav-gruppe", !offen && "zu")} data-gruppe={gruppe.schluessel}>
              {collapsed ? (
                <div className="pp-nav-gruppe-linie" aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  className="pp-nav-gruppe-kopf"
                  aria-expanded={offen}
                  aria-controls={`nav-gruppe-${gruppe.schluessel}`}
                  onClick={() => gruppeUmschalten(gruppe.schluessel)}
                  title={aktiv && !zu.has(gruppe.schluessel) ? undefined : (offen ? "Zuklappen" : "Aufklappen")}
                >
                  <span>{gruppe.label}</span>
                  {!offen && ungelesen > 0 && (
                    <span className="pp-nav-badge" title={`${ungelesen} neu in dieser Gruppe`}>{ungelesen > 99 ? "99+" : ungelesen}</span>
                  )}
                  <ChevronDown className={cn("pp-nav-gruppe-pfeil", !offen && "zu")} />
                </button>
              )}
              {offen && (
                <div id={`nav-gruppe-${gruppe.schluessel}`} className="pp-nav-gruppe-punkte">
                  {sichtbar.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) => cn(isActive && "active")}
                    >
                      <item.icon />
                      {!collapsed && <span>{item.label}</span>}
                      {!!(item.bereich && badges[item.bereich]) && (
                        <span
                          className="pp-nav-badge"
                          title={item.bereich === "vertrieb"
                            ? `${badges.vertrieb} heute im Vertrieb dran`
                            : `${badges[item.bereich!]} neu seit Ihrem letzten Besuch`}
                        >
                          {badges[item.bereich!] > 99 ? "99+" : badges[item.bereich!]}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      <div className="pp-sidebar-footer">
        <a
          href="#"
          title={collapsed ? (notify ? "Benachrichtigungen aus" : "Benachrichtigungen an") : undefined}
          onClick={(e) => { e.preventDefault(); toggleNotify() }}
        >
          {notify ? <Bell /> : <BellOff />}
          {!collapsed && <span>{notify ? "Benachrichtigungen an" : "Benachrichtigungen aus"}</span>}
        </a>
        {/* Eigenes Konto (#27) — bewusst im Fuss neben dem Logout und nicht
            in der Hauptnavigation: es ist nichts, was man taeglich braucht. */}
        <NavLink to="/konto" title={collapsed ? "Mein Konto" : undefined}>
          <KeyRound />
          {!collapsed && <span>{kontoQ.data?.benutzer || "Mein Konto"}</span>}
        </NavLink>
        <a
          href="#"
          title={collapsed ? "Logout" : undefined}
          onClick={(e) => { e.preventDefault(); logout.mutate() }}
        >
          <LogOut />
          {!collapsed && <span>Logout</span>}
        </a>
      </div>
    </aside>
    </>
  )
}

/**
 * werkXpartner Logo (weiss + orange X) — vereinfachtes SVG.
 * Das Original-SVG aus ADMIN_NAV ist zu komplex, dieses ist kompakter.
 */
function PortalLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 40"
      height="26"
      aria-label="werkXpartner"
    >
      <text
        x="0"
        y="28"
        fontFamily="Inter, sans-serif"
        fontWeight="700"
        fontSize="22"
        fill="#fff"
        letterSpacing="-0.5"
      >
        werk
      </text>
      <text
        x="58"
        y="28"
        fontFamily="Inter, sans-serif"
        fontWeight="900"
        fontSize="26"
        fill="#ff6600"
      >
        X
      </text>
      <text
        x="82"
        y="28"
        fontFamily="Inter, sans-serif"
        fontWeight="700"
        fontSize="22"
        fill="#fff"
        letterSpacing="-0.5"
      >
        partner
      </text>
    </svg>
  )
}


/**
 * Zeigt durchgehend, in welchem Mandanten man arbeitet — mit dessen Farbe —
 * und laesst hier auch wechseln.
 *
 * Ticket #11. Der Mandanten-Filter ist bereits global (`useTenantFilter`,
 * gespeichert in localStorage und URL), deshalb reicht EINE Anzeige an einer
 * Stelle, die auf jeder Seite sichtbar ist. Die Alternative waere gewesen,
 * zwanzig einzelne Auswahlfelder anzufassen.
 *
 * Nicht bloss Kosmetik: im falschen Mandanten zu arbeiten faellt oft erst auf,
 * nachdem etwas angelegt wurde.
 *
 * Ticket #43 (Marlene, 26.08.2026): „Tenant Wechsel soll in der Sidebar links
 * oben möglich sein". Das war ein kleiner Schritt — der Zustand ist ja schon
 * global, es fehlte nur der Griff. Gewechselt wurde bis dahin in einem eigenen
 * Auswahlfeld je Seite; wer auf einer Seite ohne solches Feld stand, musste
 * erst eine mit suchen.
 *
 * 🔴 Der Streifen rendert sich nicht mehr weg, wenn kein Mandant gewaehlt ist.
 * Frueher war das richtig — eine Anzeige ohne Aussage waere Rauschen gewesen.
 * Als Bedienelement muss er auch bei „alle Mandanten" dastehen, sonst kaeme
 * man von dort nie wieder in einen Mandanten hinein.
 */
function MandantStreifen({ collapsed }: { collapsed: boolean }) {
  const [slug, setSlug] = useTenantFilter()
  const farbe = tenantFarbe(slug)

  const { data } = useQuery({
    queryKey: ["tenants-namen"],
    queryFn: async (): Promise<Record<string, string>> => {
      const r = await fetch("/api/v1/tenants?include_test=true", { credentials: "include" })
      if (!r.ok) return {}
      const d = await r.json()
      return Object.fromEntries(
        (d.items || []).map((t: Record<string, unknown>) => [String(t.slug), String(t.name || t.slug)]),
      )
    },
    staleTime: 5 * 60_000,
  })

  const name = data?.[slug] || slug
  const eintraege = Object.entries(data || {}).sort((a, b) => a[1].localeCompare(b[1]))

  // Eingeklappt bleibt es bei der reinen Anzeige: ein Auswahlfeld von 36 px
  // Breite trifft niemand, und der Punkt traegt die Farbe genauso.
  if (collapsed) {
    if (!farbe) return null
    return (
      <div
        title={`Aktueller Mandant: ${name}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "8px 10px 4px", padding: "6px 0",
          borderRadius: 6, borderLeft: `3px solid ${farbe.rand}`,
          background: farbe.hintergrund, overflow: "hidden",
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: farbe.rand, flexShrink: 0,
        }} />
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 8,
        margin: "8px 10px 4px", padding: "6px 10px",
        borderRadius: 6,
        borderLeft: `3px solid ${farbe ? farbe.rand : "rgba(148,163,184,0.5)"}`,
        background: farbe ? farbe.hintergrund : "rgba(148,163,184,0.10)",
        overflow: "hidden",
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        background: farbe ? farbe.rand : "transparent",
        border: farbe ? undefined : "1px solid rgba(148,163,184,0.7)",
      }} />
      <select
        aria-label="Mandant wechseln"
        title={slug ? `Aktueller Mandant: ${name}` : "Alle Mandanten"}
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        style={{
          flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600,
          color: "#e2e8f0", background: "transparent", border: "none",
          padding: 0, cursor: "pointer", appearance: "none",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        <option value="" style={{ color: "#1e293b" }}>Alle Mandanten</option>
        {/* 🔑 Der gesetzte Wert MUSS als Option dabeisein, auch waehrend die
            Namensliste laedt oder wenn sie ihn nicht kennt. Sonst faellt das
            Feld auf „Alle Mandanten" zurueck und behauptet damit das Gegenteil
            dessen, wonach gerade gefiltert wird. */}
        {slug && !eintraege.some(([s]) => s === slug) && (
          <option value={slug} style={{ color: "#1e293b" }}>{name}</option>
        )}
        {eintraege.map(([s, n]) => (
          <option key={s} value={s} style={{ color: "#1e293b" }}>{n}</option>
        ))}
      </select>
    </div>
  )
}
